import { useEffect, useRef, useState } from 'react';
import { photoToBlob } from '../lib/image';
import type { PhotoData } from '../types';

export function ItemPhoto({ photo, photoMimeType, alt, className = '', loading = 'eager' }: { photo: PhotoData; photoMimeType?: string; alt: string; className?: string; loading?: 'eager' | 'lazy' }) {
  const [source, setSource] = useState('');
  const activeUrl = useRef('');
  const staleUrls = useRef<Set<string> | null>(null);
  const retryForPhoto = useRef<PhotoData | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(photoToBlob(photo, photoMimeType));
    if (activeUrl.current) staleUrlSet().add(activeUrl.current);
    activeUrl.current = url;
    retryForPhoto.current = null;
    setSource(url);
  }, [photo, photoMimeType]);

  useEffect(() => {
    return () => {
      const activeAtCleanup = activeUrl.current;
      const staleAtCleanup = [...(staleUrls.current ?? [])];
      // WebKit may still be decoding the source as React removes the image.
      window.setTimeout(() => {
        if (activeAtCleanup) URL.revokeObjectURL(activeAtCleanup);
        for (const url of staleAtCleanup) URL.revokeObjectURL(url);
      }, 1_000);
    };
  }, []);

  const releaseStaleUrls = () => {
    for (const url of staleUrls.current ?? []) URL.revokeObjectURL(url);
    staleUrls.current?.clear();
  };

  const retry = () => {
    if (retryForPhoto.current === photo) return;
    retryForPhoto.current = photo;
    const url = URL.createObjectURL(photoToBlob(photo, photoMimeType));
    if (activeUrl.current) staleUrlSet().add(activeUrl.current);
    activeUrl.current = url;
    setSource(url);
  };

  if (!source) return <span className={`${className} image-loading`} role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={alt ? undefined : true} />;
  return <img src={source} className={className} alt={alt} loading={loading} decoding="async" onError={retry} onLoad={releaseStaleUrls} />;

  function staleUrlSet() {
    staleUrls.current ??= new Set<string>();
    return staleUrls.current;
  }
}
